import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@clerk/clerk-react"
import { mockClient } from "../api/mockClient"
import {
  cancelSubscription,
  fetchSubscription,
  upgradeSubscription,
  createSubscriptionCheckout,
  type SubscriptionCheckoutPayload,
} from "../api/subscriptions"
import {
  fetchBillingPlans,
  fetchBillingUsage,
  changeBillingPlan,
  updateBillingAddons,
  createBillingCheckoutSession,
  createBillingPortalSession,
} from "../api/billing"
import {
  fetchQuietHours,
  setQuietHours,
  updateTenantProfile,
  disconnectStripe,
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../api/settings"
import { fetchPublicInvoice } from "../api/publicInvoice"
import { addTeamMember, listTeamMembers, removeTeamMember } from "../api/team"
import { fetchDeadLetters, fetchAdminTenants, fetchWebhookEvents, requeueDeadLetter } from "../api/admin"

import type {
  ContactLogs,
  GhostNote,
  Integrations,
  InvoiceStatus,
  PlatformHealth,
  SubscriptionSummary,
} from "../types"
import { useDemoMode } from "../demo/DemoProvider"

export function useTenant() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useQuery({
    queryKey: ["tenant", { demo }],
    queryFn: async () => {
      const token = demo ? null : await getToken()
      return mockClient.getTenant(demo, token)
    },
  })
}

export function useBillingPlans() {
  const { demo } = useDemoMode()
  return useQuery({
    queryKey: ["billingPlans", { demo }],
    queryFn: () => fetchBillingPlans(demo),
  })
}

export function useBillingUsage() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["billingUsage", { demo }],
    queryFn: async () => fetchBillingUsage(demo, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useChangeBillingPlan() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (planSlug: string) => changeBillingPlan(planSlug, demo, demo ? null : await getToken()),
    onSuccess: (data) => {
      client.setQueryData(["billingUsage", { demo }], data)
      client.invalidateQueries({ queryKey: ["billingUsage"] })
      client.invalidateQueries({ queryKey: ["tenant"] })
    },
  })
}

export function useUpdateBillingAddons() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (addonSlugs: string[]) => updateBillingAddons(addonSlugs, demo, demo ? null : await getToken()),
    onSuccess: (data) => {
      client.setQueryData(["billingUsage", { demo }], data)
      client.invalidateQueries({ queryKey: ["billingUsage"] })
    },
  })
}

export function useCreateBillingCheckoutMutation() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useMutation({
    mutationFn: async (planSlug: string) =>
      createBillingCheckoutSession(planSlug, demo, demo ? null : await getToken()),
  })
}

export function useCreateBillingPortalSessionMutation() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useMutation({
    mutationFn: async () => createBillingPortalSession(demo, demo ? null : await getToken()),
  })
}

export function useSenderStatus() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["senderStatus", { demo }],
    queryFn: async () => mockClient.getSenderStatus(demo, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useEnsureSenderReady() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { region?: string; request_whatsapp?: boolean }) =>
      mockClient.ensureSenderReady(demo, payload, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tenant"] })
      client.invalidateQueries({ queryKey: ["senderStatus"] })
    },
  })
}

export function useEventLog() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["events", { demo }],
    queryFn: async () => {
      const token = demo ? null : await getToken()
      return mockClient.getEventLog(demo, token)
    },
    enabled: demo || isSignedIn === true,
  })
}

export function useInvoices(params: { status?: InvoiceStatus | ""; query?: string }) {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useInfiniteQuery({
    queryKey: ["invoices", { demo, params }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const token = demo ? null : await getToken()
      return mockClient.getInvoices(demo, { ...params, cursor: pageParam ?? null }, token)
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: demo || isSignedIn === true,
  })
}

export function useContacts(params: { query?: string }) {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useInfiniteQuery({
    queryKey: ["contacts", { demo, params }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const token = demo ? null : await getToken()
      return mockClient.getContacts(demo, { ...params, cursor: pageParam ?? null }, token)
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: demo || isSignedIn === true,
  })
}

export function useContactLogs(contactId?: string) {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useQuery<ContactLogs>({
    queryKey: ["contactLogs", { demo, contactId }],
    queryFn: async () => {
      if (!contactId) return { chat: [], ops: [] }
      const token = demo ? null : await getToken()
      return mockClient.getContactLogs(demo, contactId, token)
    },
    enabled: Boolean(contactId),
  })
}

export function useContactNotes(contactId?: string) {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useQuery<{ ghost_notes: GhostNote[] }>({
    queryKey: ["contactNotes", { demo, contactId }],
    queryFn: async () => {
      if (!contactId) return { ghost_notes: [] }
      const token = demo ? null : await getToken()
      return mockClient.getContactNotes(demo, contactId, token) as Promise<{ ghost_notes: GhostNote[] }>
    },
    enabled: Boolean(contactId),
  })
}

export function useSaveContactNote() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      contactId: string
      payload: {
        message_id: number
        summary: string
        confidence_label?: string | null
        confidence_score?: number | null
        sources?: string[]
        details?: Record<string, unknown>
      }
    }) => {
      const token = demo ? null : await getToken()
      return mockClient.saveContactNote(demo, input.contactId, input.payload, token)
    },
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: ["contactNotes", { demo, contactId: variables.contactId }] })
    },
  })
}

export function useIntegrations() {
  const { demo } = useDemoMode()
  return useQuery<Integrations>({
    queryKey: ["integrations", { demo }],
    queryFn: () => mockClient.getIntegrations(demo),
  })
}

export function useStatusHealth() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery<PlatformHealth>({
    queryKey: ["statusHealth", { demo }],
    queryFn: async () => {
      const token = demo ? null : await getToken()
      return mockClient.getStatusHealth(demo, token) as Promise<PlatformHealth>
    },
    refetchInterval: 30000,
    enabled: demo || isSignedIn === true,
  })
}

export function useResendPayLink() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { invoiceId: string; contactId?: string; tenantId: number }) =>
      mockClient.postAction(demo, "resend_pay_link", {
        invoice_id: Number(input.invoiceId),
        contact_id: input.contactId ? Number(input.contactId) : undefined,
        tenant_id: input.tenantId,
      }, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["events"] })
    },
  })
}

export function useIssueBalanceNow() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { invoiceId: string; contactId: string; tenantId: number }) =>
      mockClient.postAction(demo, "issue_balance_now", {
        invoice_id: Number(input.invoiceId),
        contact_id: Number(input.contactId),
        tenant_id: input.tenantId,
      }, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["events"] })
    },
  })
}

export function useRefreshContext(tenantId: number) {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (contactId: string) =>
      mockClient.refreshContext(
        demo,
        { contact_id: Number(contactId), tenant_id: tenantId },
        demo ? null : await getToken(),
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["contactLogs"] })
    },
  })
}

export function useSubscription() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["subscription", { demo }],
    queryFn: async () => fetchSubscription(demo, demo ? null : await getToken()) as Promise<SubscriptionSummary>,
    enabled: demo || isSignedIn === true,
  })
}

export function useUpgradeSubscription() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { priceId?: string; quantity?: number }) =>
      upgradeSubscription(demo, input, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["subscription", { demo }] })
      client.invalidateQueries({ queryKey: ["statusHealth", { demo }] })
    },
  })
}

export function useCancelSubscription() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (atPeriodEnd: boolean) =>
      cancelSubscription(demo, { atPeriodEnd }, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["subscription", { demo }] })
      client.invalidateQueries({ queryKey: ["statusHealth", { demo }] })
    },
  })
}

export function useSubscriptionCheckout() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SubscriptionCheckoutPayload) =>
      createSubscriptionCheckout(demo, payload, demo ? null : await getToken()),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["subscription", { demo }] })
      client.invalidateQueries({ queryKey: ["statusHealth", { demo }] })
    },
  })
}

export function useUpdateTenantProfile() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name: string
      support_number?: string | null
      brand_logo_url?: string | null
      allow_web_send: boolean
      notifications?: Record<string, boolean> | null
    }) => {
      const token = demo ? null : await getToken()
      return updateTenantProfile(demo, payload, token)
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tenant"] })
    },
  })
}

export function useQuietHours() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["quietHours", { demo }],
    queryFn: async () => fetchQuietHours(demo, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useSetQuietHours() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { enabled: boolean; start_time?: string | null; end_time?: string | null; timezone?: string | null }) =>
      setQuietHours(demo, payload, demo ? null : await getToken()),
    onSuccess: () => client.invalidateQueries({ queryKey: ["quietHours"] }),
  })
}

export function useNotificationPreferences() {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["notificationPreferences", { demo }],
    queryFn: async () => {
      const token = demo ? null : await getToken()
      return fetchNotificationPreferences(demo, token)
    },
    enabled: demo || isSignedIn === true,
  })
}

export function useUpdateNotificationPreferences() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { weekly_digest: boolean; channels: Record<string, { digest_enabled: boolean; quiet_hours?: Record<string, string | null> | null }> }) => {
      const token = demo ? null : await getToken()
      return updateNotificationPreferences(demo, payload, token)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["notificationPreferences"] }),
  })
}

export function useDisconnectStripe() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => disconnectStripe(demo, demo ? null : await getToken()),
    onSuccess: () => client.invalidateQueries({ queryKey: ["tenant"] }),
  })
}

export function usePublicInvoice(token: string | undefined) {
  const { demo } = useDemoMode()
  return useQuery({
    queryKey: ["publicInvoice", { demo, token }],
    enabled: Boolean(token),
    queryFn: () => (token ? fetchPublicInvoice(token, demo) : Promise.reject(new Error("Missing token"))),
  })
}

export function useAdminDeadLetters(params: { limit?: number } = {}) {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["adminDeadLetters", { demo, params }],
    queryFn: async () => fetchDeadLetters(demo, params, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useRequeueDeadLetter() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (deadLetterId: number) =>
      requeueDeadLetter(demo, deadLetterId, demo ? null : await getToken()),
    onSuccess: () => client.invalidateQueries({ queryKey: ["adminDeadLetters"] }),
  })
}

export function useAdminWebhooks(params: { limit?: number } = {}) {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["adminWebhooks", { demo, params }],
    queryFn: async () => fetchWebhookEvents(demo, params, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useAdminTenants(params: { limit?: number } = {}) {
  const { demo } = useDemoMode()
  const { getToken, isSignedIn } = useAuth()
  return useQuery({
    queryKey: ["adminTenants", { demo, params }],
    queryFn: async () => fetchAdminTenants(demo, params, demo ? null : await getToken()),
    enabled: demo || isSignedIn === true,
  })
}

export function useTeamMembers() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  return useQuery({
    queryKey: ["teamMembers", { demo }],
    queryFn: async () => {
      const token = demo ? null : await getToken()
      return listTeamMembers(demo, token)
    },
  })
}

export function useAddTeamMember() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { phone: string; name?: string | null }) => {
      const token = demo ? null : await getToken()
      return addTeamMember(demo, payload, token)
    },
    onSuccess: (data) => {
      client.setQueryData(["teamMembers", { demo }], data)
    },
  })
}

export function useRemoveTeamMember() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (memberId: number) => {
      const token = demo ? null : await getToken()
      return removeTeamMember(demo, memberId, token)
    },
    onSuccess: (data) => {
      client.setQueryData(["teamMembers", { demo }], data)
    },
  })
}

export { useContactMessages, useSendContactMessage } from "./useMessages"
